import React from "react";

// Scalar API Reference, loaded from the standalone CDN bundle so it adds no weight
// to the CRA/webpack build. If the bundle can't load (offline/CSP/API drift), we
// fall back to the static docs passed via `fallback` — the app never breaks on this.
const SCRIPT_ID = "scalar-api-reference-script";
const SCRIPT_SRC = "https://cdn.jsdelivr.net/npm/@scalar/api-reference";

interface Props {
    /** OpenAPI spec URL. Used when `spec` (inline content) is not provided. */
    specUrl?: string;
    /** Inline OpenAPI document (YAML/JSON string). Takes precedence over `specUrl`. */
    spec?: string;
    fallback?: React.ReactNode;
}

export const ScalarDocs: React.FunctionComponent<Props> = ({ specUrl, spec, fallback }) => {
    const containerRef = React.useRef<HTMLDivElement>(null);
    const [failed, setFailed] = React.useState<boolean>(false);
    const [ready, setReady] = React.useState<boolean>(false);

    React.useEffect(() => {
        let cancelled = false;
        let timer = 0;

        const mount = () => {
            if (cancelled) return;
            const Scalar = (window as any).Scalar;
            if (Scalar && typeof Scalar.createApiReference === 'function' && containerRef.current) {
                try {
                    // Prefer inline content (lets the caller patch the spec, e.g.
                    // environment-specific servers) and fall back to fetching by URL.
                    const config = spec ? { content: spec } : { url: specUrl };
                    Scalar.createApiReference(containerRef.current, config);
                    setReady(true);
                } catch {
                    setFailed(true);
                }
            } else {
                setFailed(true);
            }
        };

        if ((window as any).Scalar) {
            mount();
            return () => { cancelled = true; };
        }

        let script = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;
        const onLoad = () => mount();
        const onError = () => { if (!cancelled) setFailed(true); };

        if (!script) {
            script = document.createElement('script');
            script.id = SCRIPT_ID;
            script.src = SCRIPT_SRC;
            document.body.appendChild(script);
        }
        script.addEventListener('load', onLoad);
        script.addEventListener('error', onError);
        // Give up if the CDN is slow/blocked.
        timer = window.setTimeout(() => {
            if (!cancelled && !(window as any).Scalar) setFailed(true);
        }, 8000);

        return () => {
            cancelled = true;
            window.clearTimeout(timer);
            script?.removeEventListener('load', onLoad);
            script?.removeEventListener('error', onError);
        };
    }, [specUrl, spec]);

    if (failed) {
        return <>{fallback ?? <p className="text-sm text-gray-500">Unable to load the interactive API reference.</p>}</>;
    }

    return (
        <div className="h-full w-full">
            {!ready && <p className="text-sm text-gray-500 dark:text-gray-400">Loading interactive API reference…</p>}
            <div ref={containerRef} className="h-full w-full" />
        </div>
    );
};
